// ── Onboarding Ultra Simple para Radios ──
// Bloque 47 — 5 pasos, menos de 5 minutos

// ── Paso 1 — Datos Básicos ──
export const stepBasicData = {
  id: 'basic-data',
  stepNumber: 1,
  title: 'Datos básicos',
  subtitle: 'Contanos quién sos en menos de 1 minuto',
  duration: '~45s',
  icon: 'ri-user-add-line',
  color: 'emerald',
  gradient: 'from-emerald-500 to-teal-500',
  textColor: 'text-emerald-600',
  bgColor: 'bg-emerald-50',
  fields: [
    { name: 'name', label: 'Nombre de la radio', type: 'text', placeholder: 'Ej: Radio La Kalle', required: true, maxLength: 60 },
    { name: 'slogan', label: 'Eslogan', type: 'text', placeholder: 'Ej: Donde vive tu música', required: false, maxLength: 120 },
    { name: 'country', label: 'País', type: 'select', required: true, options: [
      'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica', 'Cuba',
      'Ecuador', 'El Salvador', 'España', 'Estados Unidos', 'Guatemala', 'Honduras',
      'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú', 'Portugal', 'Puerto Rico',
      'República Dominicana', 'Uruguay', 'Venezuela',
    ]},
    { name: 'city', label: 'Ciudad', type: 'text', placeholder: 'Ej: Buenos Aires', required: true, maxLength: 80 },
    { name: 'category', label: 'Categoría', type: 'select', required: true, options: [
      'Música', 'Noticias', 'Deportes', 'Entretenimiento', 'Cultural',
      'Religiosa', 'Comunitaria', 'Juvenil', 'Romántica', 'Rock',
      'Electrónica', 'Tropical', 'Regional', 'Jazz & Blues', 'Clásica',
      'Hip Hop & Urbano', 'Podcast', 'Variada',
    ]},
    { name: 'language', label: 'Idioma principal', type: 'select', required: true, options: ['Español', 'Portugués', 'Inglés', 'Francés', 'Italiano', 'Otro'] },
  ],
  tip: 'Completá con datos reales. Esto aparece en tu perfil público.',
};

// ── Paso 2 — Streaming URL ──
export const streamTypes = [
  { id: 'icecast', label: 'Servidor de streaming', icon: 'ri-cloud-line', description: 'Servidor de streaming estándar', color: 'sky' },
  { id: 'shoutcast', label: 'Servidor alternativo', icon: 'ri-broadcast-line', description: 'Servidor de streaming alternativo', color: 'amber' },
  { id: 'autodj', label: 'AutoDJ', icon: 'ri-disc-line', description: 'Servicio AutoDJ / Centova Cast', color: 'violet' },
  { id: 'other', label: 'Otro', icon: 'ri-link', description: 'Cualquier stream HTTP/HTTPS', color: 'rose' },
] as const;

export const bitrateOptions = [
  { value: '32', label: '32 kbps', quality: 'Baja' },
  { value: '64', label: '64 kbps', quality: 'Media-Baja' },
  { value: '96', label: '96 kbps', quality: 'Media' },
  { value: '128', label: '128 kbps', quality: 'Buena' },
  { value: '192', label: '192 kbps', quality: 'Alta' },
  { value: '256', label: '256 kbps', quality: 'Muy Alta' },
  { value: '320', label: '320 kbps', quality: 'Excelente' },
] as const;

export const stepStreamingUrl = {
  id: 'streaming-url',
  stepNumber: 2,
  title: 'Streaming URL',
  subtitle: 'Conectá tu transmisión en 30 segundos',
  duration: '~30s',
  icon: 'ri-broadcast-line',
  color: 'sky',
  gradient: 'from-sky-500 to-cyan-500',
  textColor: 'text-sky-600',
  bgColor: 'bg-sky-50',
  tip: 'Asegurate de que tu stream esté funcionando antes de probar la conexión.',
};

// ── Paso 3 — Frecuencia RFW ──
export interface AvailableFrequency {
  frequency: string;
  type: 'standard' | 'premium' | 'recommended';
  label: string;
  description: string;
  price?: string;
  badge?: string;
}

export const availableFrequencies: AvailableFrequency[] = [
  { frequency: '88.1', type: 'standard', label: '88.1 RFW', description: 'Frecuencia estándar disponible' },
  { frequency: '92.5', type: 'recommended', label: '92.5 RFW', description: 'Fácil de recordar, excelente visibilidad', badge: '🌟 Recomendada' },
  { frequency: '94.7', type: 'standard', label: '94.7 RFW', description: 'Frecuencia estándar disponible' },
  { frequency: '97.3', type: 'premium', label: '97.3 RFW', description: 'Frecuencia premium — alta visibilidad', price: 'Plan Pro', badge: '💎 Premium' },
  { frequency: '99.9', type: 'recommended', label: '99.9 RFW', description: 'La más solicitada, gran recordación', badge: '🔥 Top' },
  { frequency: '101.5', type: 'standard', label: '101.5 RFW', description: 'Frecuencia estándar disponible' },
  { frequency: '103.7', type: 'premium', label: '103.7 RFW', description: 'Frecuencia premium — exclusiva', price: 'Plan Pro', badge: '💎 Premium' },
  { frequency: '105.3', type: 'standard', label: '105.3 RFW', description: 'Frecuencia estándar disponible' },
  { frequency: '107.9', type: 'recommended', label: '107.9 RFW', description: 'Último dial, siempre recordada', badge: '⭐ Destacada' },
];

export const stepFrequency = {
  id: 'frequency',
  stepNumber: 3,
  title: 'Frecuencia RFW',
  subtitle: 'Elegí tu lugar en el dial web',
  duration: '~30s',
  icon: 'ri-radar-line',
  color: 'amber',
  gradient: 'from-amber-500 to-orange-500',
  textColor: 'text-amber-600',
  bgColor: 'bg-amber-50',
  tip: 'Las frecuencias premium requieren Plan Pro. Las recomendadas son las más fáciles de recordar para tus oyentes.',
};

// ── Paso 4 — Primera Cabina ──
export interface CabinaOption {
  id: string;
  label: string;
  icon: string;
  description: string;
  defaultEnabled: boolean;
  category: 'core' | 'social' | 'engagement';
}

export const cabinaOptions: CabinaOption[] = [
  { id: 'active', label: 'Activar Cabina', icon: 'ri-toggle-line', description: 'Encendé tu cabina para recibir mensajes de oyentes en vivo', defaultEnabled: true, category: 'core' },
  { id: 'moderation', label: 'Moderación', icon: 'ri-shield-check-line', description: 'Filtrá mensajes ofensivos automáticamente', defaultEnabled: true, category: 'core' },
  { id: 'greetings', label: 'Saludos', icon: 'ri-user-voice-line', description: 'Permití que los oyentes envíen saludos al aire', defaultEnabled: true, category: 'social' },
  { id: 'songs', label: 'Canciones', icon: 'ri-music-line', description: 'Dejá que los oyentes pidan canciones', defaultEnabled: true, category: 'social' },
  { id: 'polls', label: 'Encuestas', icon: 'ri-bar-chart-line', description: 'Creá encuestas rápidas para tu audiencia', defaultEnabled: false, category: 'engagement' },
  { id: 'contests', label: 'Concursos', icon: 'ri-trophy-line', description: 'Activá concursos para tus oyentes', defaultEnabled: false, category: 'engagement' },
];

export const stepCabina = {
  id: 'cabina',
  stepNumber: 4,
  title: 'Primera Cabina',
  subtitle: 'Configurá tu espacio de interacción',
  duration: '~40s',
  icon: 'ri-message-3-line',
  color: 'violet',
  gradient: 'from-violet-500 to-purple-500',
  textColor: 'text-violet-600',
  bgColor: 'bg-violet-50',
  tip: 'Podés cambiar todo esto después desde tu panel de administración. Empezá simple.',
};

// ── Paso 5 — Finalizar ──
export interface FinalResult {
  icon: string;
  label: string;
  description: string;
  path: string;
  color: string;
}

export const finalResults: FinalResult[] = [
  { icon: 'ri-radio-line', label: 'Radio publicada', description: 'Tu radio ya aparece en el directorio de RFW', path: '/explorar', color: 'emerald' },
  { icon: 'ri-qr-code-line', label: 'QR generado', description: 'Compartilo en redes, imprimilo, pegá donde quieras', path: '/admin/settings/qr', color: 'violet' },
  { icon: 'ri-user-star-line', label: 'Perfil creado', description: 'Tu página pública con toda la info de tu radio', path: '/admin/profile', color: 'amber' },
  { icon: 'ri-group-line', label: 'Club creado', description: 'Tu comunidad ya tiene su espacio en RFW', path: '/admin/community', color: 'rose' },
];

export const stepFinalize = {
  id: 'finalize',
  stepNumber: 5,
  title: 'Finalizar',
  subtitle: 'Tu radio ya está en RFW',
  duration: '~10s',
  icon: 'ri-check-double-line',
  color: 'emerald',
  gradient: 'from-emerald-500 to-green-500',
  textColor: 'text-emerald-600',
  bgColor: 'bg-emerald-50',
};

// ── Stats ──
export const onboardingRadiosStats = {
  totalRadiosOnboarded: 1247,
  avgCompletionTime: '3:42',
  completionRate: 91.4,
  radiosActiveToday: 38,
  fastestOnboarding: '1:28',
  stepsWithMostAbandonment: 'Paso 2 (Streaming URL)',
};

export const successStories = [
  { name: 'Radio La Kalle', frequency: '99.9', city: 'Bogotá', time: '2:15', testimonial: 'No podía creer lo rápido que fue. En 2 minutos ya tenía mi frecuencia.' },
  { name: 'FM del Sol', frequency: '92.5', city: 'Buenos Aires', time: '3:08', testimonial: 'El probador de conexión me salvó. Detecté un error en mi stream antes de publicar.' },
  { name: 'Estación Latina', frequency: '88.1', city: 'Ciudad de México', time: '3:45', testimonial: 'La cabina ya venía configurada. Solo tuve que elegir mi frecuencia y listo.' },
];

// ── Rules ──
export const onboardingRadiosRules = [
  {
    id: 'rule-1',
    icon: 'ri-timer-line',
    title: 'No más de 5 pasos',
    color: 'emerald',
    description: 'El onboarding completo se completa en menos de 5 minutos. Cada paso está optimizado para ser rápido e intuitivo.',
  },
  {
    id: 'rule-2',
    icon: 'ri-plug-line',
    title: 'Stream funcionando antes de publicar',
    color: 'sky',
    description: 'El botón "Probar conexión" verifica que tu stream esté activo antes de continuar. Si no funciona, no avances.',
  },
  {
    id: 'rule-3',
    icon: 'ri-rocket-line',
    title: 'Publicación inmediata',
    color: 'violet',
    description: 'Una vez completados los 5 pasos, la radio aparece instantáneamente en RFW. Sin esperas. Sin aprobaciones.',
  },
];

// ── Distribución de tiempos ──
export const stepTimeDistribution = [
  { step: 1, label: 'Datos básicos', seconds: 45, color: 'emerald' },
  { step: 2, label: 'Streaming URL', seconds: 30, color: 'sky' },
  { step: 3, label: 'Frecuencia RFW', seconds: 30, color: 'amber' },
  { step: 4, label: 'Primera Cabina', seconds: 40, color: 'violet' },
  { step: 5, label: 'Finalizar', seconds: 10, color: 'emerald' },
];

// ── Países sugeridos para ciudad ──
export const countryCities: Record<string, string[]> = {
  'Argentina': ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Tucumán'],
  'México': ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana'],
  'Colombia': ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena'],
  'España': ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao'],
  'Chile': ['Santiago', 'Valparaíso', 'Concepción', 'La Serena'],
  'Perú': ['Lima', 'Arequipa', 'Trujillo', 'Cusco'],
};

// ── Helpers ──
export const getStepConfig = (step: number) => {
  const configs = [stepBasicData, stepStreamingUrl, stepFrequency, stepCabina, stepFinalize];
  return configs[step - 1] ?? stepBasicData;
};

export const getFrequenciesByType = (type: 'standard' | 'premium' | 'recommended') =>
  availableFrequencies.filter((f) => f.type === type);

export const getCabinaOptionsByCategory = (category: 'core' | 'social' | 'engagement') =>
  cabinaOptions.filter((o) => o.category === category);

export const getTimeDistributionTotal = () =>
  stepTimeDistribution.reduce((acc, s) => acc + s.seconds, 0);